var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { boundMethod } from 'autobind-decorator';
import isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';
import { forwardRef, PureComponent } from 'react';
import { ajaxSend } from 'api/api';
import { createTemplate, getTemplates, readTemplateById, removeTemplate, removeTemplateTranslations, updateTemplate, updateTemplateTranslations, } from './api/templates';
const DEFAULT_FETCH_THROTTLE = 300;
/** The position of the new data, used when `settings.infinite` is `true` */
var NewDataPosition;
(function (NewDataPosition) {
    NewDataPosition[NewDataPosition["PreviousPage"] = -1] = "PreviousPage";
    NewDataPosition[NewDataPosition["Replace"] = 0] = "Replace";
    NewDataPosition[NewDataPosition["NextPage"] = 1] = "NextPage";
})(NewDataPosition || (NewDataPosition = {}));
export const withTemplates = (WrappedComponent, settings = { infinite: false }) => {
    class WithTemplates extends PureComponent {
        constructor(props) {
            super(props);
            this.timeout = null;
            this.state = {
                items: [],
                params: Object.assign({ limit: 25 }, settings.initialParams),
                nextPagePath: null,
                previousPagePath: null,
                getItemsAsyncStatus: 'succeeded',
                createAsyncStatus: 'succeeded',
                updateAsyncStatus: 'succeeded',
                readTemplateByIdAsyncStatus: 'succeeded',
                removeAsyncStatus: 'succeeded',
                updateTranslationsAsyncStatus: 'succeeded',
                removeTranslationsAsyncStatus: 'succeeded',
            };
            this.memoizedGetInjectedProps = memoize(this.memoizedGetInjectedProps, isEqual).bind(this); // We have to bind it like this. Using @boundMethod won't work
        }
        componentWillUnmount() {
            if (this.timeout) {
                clearTimeout(this.timeout);
            }
        }
        getItems(position = NewDataPosition.Replace) {
            this.setState({ getItemsAsyncStatus: 'loading' });
            const params = Object.assign({}, this.state.params);
            if (params === null || params === void 0 ? void 0 : params.search) {
                delete params.sort_by; // We do not support sort and search in the same query
            }
            return getTemplates(params)
                .then((resp) => this.handleQueryResolve(resp, position))
                .catch(this.handleQueryReject);
        }
        getItemsWithPath(path, position) {
            this.setState({ getItemsAsyncStatus: 'loading' });
            return ajaxSend({ path })
                .then((resp) => this.handleQueryResolve(resp, position))
                .catch(this.handleQueryReject);
        }
        nextPage() {
            const { nextPagePath } = this.state;
            if (nextPagePath) {
                return this.getItemsWithPath(nextPagePath, NewDataPosition.NextPage);
            }
            throw new Error('Next page does not exist');
        }
        previousPage() {
            const { previousPagePath } = this.state;
            if (previousPagePath) {
                return this.getItemsWithPath(previousPagePath, NewDataPosition.PreviousPage);
            }
            throw new Error('Previous page does not exist');
        }
        create(template) {
            this.setState({ createAsyncStatus: 'loading' });
            return createTemplate(template)
                .then((response) => {
                this.setState({ createAsyncStatus: 'succeeded' });
                return response;
            })
                .catch((err) => {
                this.setState({ createAsyncStatus: 'failed' });
                throw err;
            });
        }
        update(templateId, payload) {
            this.setState({ updateAsyncStatus: 'loading' });
            return updateTemplate(templateId, payload)
                .then((response) => {
                this.setState({ updateAsyncStatus: 'succeeded' });
                return response;
            })
                .catch((err) => {
                this.setState({ updateAsyncStatus: 'failed' });
                throw err;
            });
        }
        readTemplateById(templateId) {
            this.setState({ readTemplateByIdAsyncStatus: 'loading' });
            return readTemplateById(templateId)
                .then((response) => {
                this.setState({
                    readTemplateByIdAsyncStatus: 'succeeded',
                    items: this.state.items.concat(this.getNormalizedItems(response ? [response.data] : [])),
                });
                return response;
            })
                .catch((err) => {
                this.setState({ readTemplateByIdAsyncStatus: 'failed' });
                throw err;
            });
        }
        remove(templateId) {
            this.setState({ removeAsyncStatus: 'loading' });
            return removeTemplate(templateId)
                .then((response) => {
                this.setState({ removeAsyncStatus: 'succeeded' });
                return response;
            })
                .catch((err) => {
                this.setState({ removeAsyncStatus: 'failed' });
                throw err;
            });
        }
        updateTranslations(templateId, payload) {
            this.setState({ updateTranslationsAsyncStatus: 'loading' });
            return updateTemplateTranslations(templateId, payload)
                .then((response) => {
                this.setState({ updateTranslationsAsyncStatus: 'succeeded' });
                return response;
            })
                .catch((err) => {
                this.setState({ updateTranslationsAsyncStatus: 'failed' });
                throw err;
            });
        }
        removeTranslations(templateId, locales) {
            this.setState({ removeTranslationsAsyncStatus: 'loading' });
            return removeTemplateTranslations(templateId, locales)
                .then((response) => {
                this.setState({ removeTranslationsAsyncStatus: 'succeeded' });
                return response;
            })
                .catch((err) => {
                this.setState({ removeTranslationsAsyncStatus: 'failed' });
                throw err;
            });
        }
        handleParamsChange(params, fetchThrottle = DEFAULT_FETCH_THROTTLE) {
            return new Promise((resolve, reject) => {
                this.setState((state) => ({ params: Object.assign(Object.assign({}, state.params), params) }), () => {
                    if (fetchThrottle >= 0) {
                        if (this.timeout) {
                            clearTimeout(this.timeout);
                        }
                        this.timeout = setTimeout(() => {
                            resolve(this.getItems());
                        }, fetchThrottle);
                    }
                    else {
                        resolve();
                    }
                });
            });
        }
        getPaginatedItems(newItems, oldItems, position = NewDataPosition.Replace) {
            if (!settings.infinite || position === NewDataPosition.Replace) {
                return newItems;
            }
            if (position === NewDataPosition.NextPage) {
                return oldItems.concat(newItems);
            }
            if (position === NewDataPosition.PreviousPage) {
                return newItems.concat(oldItems);
            }
            return [];
        }
        getNormalizedItems(items) {
            if (settings.itemNormalizer === undefined) {
                // In this case we are sure that NormalizedTemplate is actually Template
                // There might be some complicated ways to avoid this casting, but it doesn't really worth it
                return items;
            }
            // we have to cast this to make typescript silent.
            // we know that `settings.itemNormalizer` is not `undefined`, because we checked it before,
            // but typescript doesn't understand that, because we are calling it inside map function!
            const itemNormalizer = settings.itemNormalizer;
            return items.map((item, index) => itemNormalizer(item, this.state.params, index));
        }
        handleQueryResolve(response, position = NewDataPosition.Replace) {
            return new Promise((resolve) => {
                this.setState((state) => ({
                    getItemsAsyncStatus: 'succeeded',
                    items: this.getPaginatedItems(this.getNormalizedItems(response.data.templates), state.items, position),
                    nextPagePath: response.meta.next_page,
                    previousPagePath: response.meta.prev_page,
                }), () => resolve(Object.assign(Object.assign({}, response), { data: Object.assign(Object.assign({}, response.data), { templates: this.state.items }) })));
            });
        }
        handleQueryReject(error) {
            this.setState({
                getItemsAsyncStatus: 'failed',
            });
            return error;
        }
        getInjectedProps(props, state) {
            const { items, params, nextPagePath, previousPagePath, getItemsAsyncStatus, createAsyncStatus, updateAsyncStatus, readTemplateByIdAsyncStatus, removeAsyncStatus, updateTranslationsAsyncStatus, removeTranslationsAsyncStatus, } = state;
            return this.memoizedGetInjectedProps(params, items, getItemsAsyncStatus, createAsyncStatus, updateAsyncStatus, readTemplateByIdAsyncStatus, removeAsyncStatus, updateTranslationsAsyncStatus, removeTranslationsAsyncStatus, nextPagePath, previousPagePath);
        }
        memoizedGetInjectedProps(params, items, getItemsAsyncStatus, createAsyncStatus, updateAsyncStatus, getAsyncStatus, removeAsyncStatus, updateTranslationsAsyncStatus, removeTranslationsAsyncStatus, nextPagePath, previousPagePath) {
            return {
                params: params,
                items: items,
                getItemsAsyncStatus: getItemsAsyncStatus,
                createAsyncStatus: createAsyncStatus,
                updateAsyncStatus: updateAsyncStatus,
                getAsyncStatus: getAsyncStatus,
                removeAsyncStatus: removeAsyncStatus,
                updateTranslationsAsyncStatus: updateTranslationsAsyncStatus,
                removeTranslationsAsyncStatus: removeTranslationsAsyncStatus,
                hasNextPage: nextPagePath !== null,
                hasPreviousPage: previousPagePath !== null,
                getItems: this.getItems,
                paramsChange: this.handleParamsChange,
                nextPage: this.nextPage,
                previousPage: this.previousPage,
                create: this.create,
                update: this.update,
                readTemplateById: this.readTemplateById,
                remove: this.remove,
                updateTranslations: this.updateTranslations,
                removeTranslations: this.removeTranslations,
            };
        }
        render() {
            const _a = this.props, { forwardedRef } = _a, props = __rest(_a, ["forwardedRef"]);
            return (_jsx(WrappedComponent, Object.assign({}, props, { templates: this.getInjectedProps(this.props, this.state), ref: forwardedRef })));
        }
    }
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "getItems", null);
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "getItemsWithPath", null);
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "nextPage", null);
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "previousPage", null);
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "create", null);
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "update", null);
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "readTemplateById", null);
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "remove", null);
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "updateTranslations", null);
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "removeTranslations", null);
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "handleParamsChange", null);
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "handleQueryResolve", null);
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "handleQueryReject", null);
    __decorate([
        boundMethod
    ], WithTemplates.prototype, "getInjectedProps", null);
    // eslint-disable-next-line react/display-name
    return forwardRef((props, ref) => _jsx(WithTemplates, Object.assign({}, props, { forwardedRef: ref })));
};
